import React, {useEffect} from 'react'
import * as Security from './api/archibus/SecurityApi'
import {fetchIsRoomReservable} from './api/room'
import {
  LOGON_CONTAINER,
  BUILDING_LIST,
  DASHBOARD,
  CONFERENCE_ROOM
} from './constants/const'

import AppRouter from './AppRouter'
import {decodeUrlParameters} from './utils/helpers'
import {
  useUserDispatch,
  fetchAndSetUserDefaultLocation,
  fetchAndSetUserInfo
} from './context/UserContext'
import {useUIDispatch} from './context/UIContext'
import {fetchParameters, useConfigDispatch} from './context/ConfigContext'
import {useLocaleDispatch, setUserLocale} from './context/LocaleContext'
import {getGuestUserAccount} from './utils/authentication'

const checkUserSession = () => {
  let project

  // TODO: Why do we need the project to check the session?
  return Security.getProjects()
    .then(projects => {
      project = projects[0].id
      return Security.isOpenSession()
    })
    .then(isOpenSession => {
      let result = {
        project,
        isOpenSession
      }
      return Promise.resolve(result)
    })
}

export const initializeUser = async ({
  userDispatch,
  uiDispatch,
  configDispatch,
  localeDispatch
}) => {
  let content = DASHBOARD

  const result = await checkUserSession()
  const {isOpenSession} = result

  if (!isOpenSession) {
    return LOGON_CONTAINER
  } else {
    const userInfo = await fetchAndSetUserInfo({userDispatch})

    await setUserLocale({userInfo, localeDispatch})

    const {employee} = userInfo

 //   const {blId, flId, rmId} = employee
	let blId = null
	let flId = null
	const rmId = null

    const defaultLocation = await fetchAndSetUserDefaultLocation({
      userDispatch,
      blId,
      flId,
      rmId
    })

    if (defaultLocation.isValid) {
      const {blId, flId} = defaultLocation.location
      uiDispatch({
        type: 'setActiveFloor',
        payload: {blId, flId}
      })
      uiDispatch({
        type: 'setHomeFloor',
        payload: {blId, flId}
      })
    } else {
		const urlParamString = window.location.search
		if (urlParamString && urlParamString != "") {
		  const urlParams = new URLSearchParams(urlParamString)
		  if (urlParams.has("blId")) {
			  blId = urlParams.get("blId")
			  if (urlParams.has("flId")) {
				  flId = urlParams.get("flId")
			  }
			  else {
				  flId = "01"
			  }
		  }
		  else {
			blId = ""
			flId = ""
		  }
		}
		else {
		//	const {blId, flId} = {blId: "", flId: ""}
			blId = ""
			flId = ""
		}
        uiDispatch({
          type: 'setActiveFloor',
          payload: {blId, flId}
        })
		const homeBlId = ""
		const homeFlId = ""
        uiDispatch({
          type: 'setHomeFloor',
          payload: {blId: homeBlId, flId: homeFlId}
        })
    }

    await fetchParameters(configDispatch)
    return content
  }
}

const initializeKiosk = async ({
  kioskId,
  uiDispatch,
  userDispatch,
  localeDispatch,
  configDispatch
}) => {
  let id = JSON.parse(kioskId)

  const {userName, password} = getGuestUserAccount()
  uiDispatch({type: 'setKioskId', payload: id})
  uiDispatch({type: 'setIsKiosk', payload: true})

  let content = 'KIOSK_CONTAINER'

  let result = await checkUserSession()

  const {project, isOpenSession} = result

  if (isOpenSession) {
    await Security.logout()
  }

  await Security.login(userName, password, project)

  const userInfo = await fetchAndSetUserInfo({userDispatch})

  await setUserLocale({userInfo, localeDispatch})

  let {bl_id, fl_id, rm_id, eq_std} = JSON.parse(kioskId)

  if (eq_std === 'KIOSK-CONFERENCE') {
    content = CONFERENCE_ROOM
  }

  uiDispatch({
    type: 'setActiveFloor',
    payload: {blId: bl_id, flId: fl_id}
  })
  uiDispatch({type: 'setHomeFloor', payload: {blId: bl_id, flId: fl_id}})

  await fetchAndSetUserDefaultLocation({
    userDispatch,
    blId: bl_id,
    flId: fl_id,
    rmId: rm_id
  })

  await fetchParameters(configDispatch)

  return content
}

const initializeEqQrCode = async ({
  userDispatch,
  localeDispatch,
  configDispatch
}) => {
  const {userName, password} = getGuestUserAccount()
  const {isOpenSession, project} = await checkUserSession()

  if (!isOpenSession) {
    await Security.login(userName, password, project)
  }

  const userInfo = await fetchAndSetUserInfo({userDispatch})
  const {employee} = userInfo
  const {blId, flId, rmId} = employee
  await fetchAndSetUserDefaultLocation({
    userDispatch,
    blId,
    flId,
    rmId
  })
  await setUserLocale({userInfo, localeDispatch})
  await fetchParameters(configDispatch)
}

const initializeRoomQrCode = async ({
  queryParams,
  userDispatch,
  localeDispatch,
  uiDispatch,
  configDispatch
}) => {
  let content = 'ROOM_QR'
  const {userName, password} = getGuestUserAccount()
  const {isOpenSession, project} = await checkUserSession()

  if (!isOpenSession) {
    await Security.login(userName, password, project)
  }

  const {qrParameters} = queryParams
  const {bl_id, fl_id, rm_id} = qrParameters

  const userInfo = await fetchAndSetUserInfo({userDispatch})
  await fetchAndSetUserDefaultLocation({
    userDispatch,
    blId: bl_id,
    flId: fl_id,
    rmId: rm_id
  })
  await setUserLocale({userInfo, localeDispatch})

  const isReservable = await fetchIsRoomReservable({
    blId: bl_id,
    flId: fl_id,
    rmId: rm_id
  })

  if (isReservable) {
    uiDispatch({type: 'setKioskId', payload: {bl_id, fl_id, rm_id}})
    uiDispatch({type: 'setIsKiosk', payload: false})
    content = 'CONFERENCE_ROOM'
  }

  await fetchParameters(configDispatch)

  return content
}

const App = () => {
  const userDispatch = useUserDispatch()
  const uiDispatch = useUIDispatch()
  const configDispatch = useConfigDispatch()
  const localeDispatch = useLocaleDispatch()

  useEffect(
    () => {
      let kioskId = localStorage.getItem('archibus_kiosk_id')

      // Remove query parameters if the app is in kiosk mode
      if (kioskId) {
        let uri = location.protocol + '//' + location.host + location.pathname
        window.history.replaceState({}, document.title, uri)
      }

      let queryParams = decodeUrlParameters({
        searchString: window.location.search,
        kioskId
      })
      ;(async () => {
        let content = DASHBOARD
        let props = {}
        let parentPage = {}

        if (kioskId && queryParams.type === 'KIOSK') {
          content = await initializeKiosk({
            kioskId,
            localeDispatch,
            uiDispatch,
            userDispatch,
            configDispatch
          })
        }

        if (queryParams.type === 'QR-EQ') {
          await initializeEqQrCode({
            userDispatch,
            localeDispatch,
            configDispatch
          })
          content = 'EQUIPMENT_QR'
          props = {eqId: queryParams.qrParameters.eq_id}
        }

        if (queryParams.type === 'QR-ROOM') {
          content = await initializeRoomQrCode({
            queryParams,
            userDispatch,
            localeDispatch,
            uiDispatch,
            configDispatch
          })
        }

        if (queryParams.type === 'DASHBOARD') {
          content = await initializeUser({
            uiDispatch,
            configDispatch,
            userDispatch,
            localeDispatch
          })
        }

        if (queryParams.type === 'CATALOG') {
          content = await initializeUser({
            uiDispatch,
            configDispatch,
            userDispatch,
            localeDispatch
          })

          content = content === LOGON_CONTAINER ? content : 'SERVICE_CATALOG'
        }

        uiDispatch({type: 'setContent', payload: {content, props, parentPage}})
      })()
    },
    [uiDispatch, configDispatch, userDispatch, localeDispatch]
  )

  return <AppRouter />
}

export default App
